import type { DraftPhase } from './builder/types';
import { tasksOfWorkflow } from './workflowTasks';
import type { WorkflowScopedTask } from './workflowTasks';

/** A task, with the step id it sits on. See `workflowTasks.ts`. */
export type PositionedTask = WorkflowScopedTask & { position: string };

/** One step that cannot go, and the work that is standing on it. */
export type BlockedStep = {
  stepName: string;
  taskTitles: string[];
};

/**
 * What deleting a phase would do, decided before anything is deleted.
 *
 * - `empty` — nothing to lose, so nothing to ask about.
 * - `blocked` — at least one step has a task on it. Refused outright.
 * - `confirm` — steps would be destroyed, and the author is asked first.
 */
export type PhaseDeletion =
  | { kind: 'empty' }
  | { kind: 'blocked'; blocked: BlockedStep[] }
  | { kind: 'confirm'; stepNames: string[] };

/**
 * Whether a phase can be deleted, and what has to be said if so.
 *
 * The service already refuses to save a document that removes a step a task is
 * positioned on — `WORKFLOW_STEPS_IN_USE`, raised before any row is deleted and
 * naming both the steps and the tasks. That refusal is the authority and it
 * stays. What it cannot do is arrive on time: the builder edits a local draft,
 * so the author deletes a phase, keeps working, and learns at Save that a change
 * they made twenty minutes ago was never possible.
 *
 * This is the same question asked early, against the workspace aggregate the
 * page has already loaded — arithmetic rather than a request, exactly as
 * `deletionSummary` does it for deleting a whole workflow.
 *
 * It can be stale: someone else can create a task between this check and the
 * save. That is why it is advisory and the server still decides. Being
 * occasionally too permissive is fine — the save refuses. Being wrong the other
 * way would be worse, so a task whose `position` matches is always counted.
 *
 * Scoped to the workflow first — see `tasksOfWorkflow` for why.
 */
export function phaseDeletion(
  phase: DraftPhase,
  workflowId: string,
  tasks: ReadonlyArray<PositionedTask>,
): PhaseDeletion {
  if (phase.steps.length === 0) return { kind: 'empty' };

  const mine = tasksOfWorkflow(tasks, workflowId);
  const blocked = phase.steps
    .map((step) => ({
      stepName: step.name,
      taskTitles: mine
        .filter((task) => task.position === step.id)
        .map((task) => task.title),
    }))
    .filter((entry) => entry.taskTitles.length > 0);

  if (blocked.length > 0) return { kind: 'blocked', blocked };
  return { kind: 'confirm', stepNames: phase.steps.map((step) => step.name) };
}
